import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search, ChevronDown, ChevronUp, Trash2, Edit2, ExternalLink, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { BrandLogo, EmptyState, Skeleton, ClearFiltersButton } from "../../components/common";
import LogoUploader from "../../components/LogoUploader";
import { useImpersonate } from "../../lib/impersonate";
import { toast } from "sonner";

export default function BrandsPage() {
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [openTeamId, setOpenTeamId] = useState(null);
  const [showBrandModal, setShowBrandModal] = useState(null);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const { previewAs } = useImpersonate();

  const load = async () => {
    setLoading(true);
    try {
      const [b, t, u, c] = await Promise.all([
        api.get("/brands"),
        api.get("/teams"),
        api.get("/users"),
        api.get("/campaigns"),
      ]);
      setBrands(b.data.brands);
      setTeams(t.data.teams);
      setUsers(u.data.users);
      setCampaigns(c.data.campaigns);
      if (!selected && b.data.brands.length) setSelected(b.data.brands[0].id);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => brands.filter((b) => !search || b.name.toLowerCase().includes(search.toLowerCase())), [brands, search]);
  const selectedBrand = brands.find((b) => b.id === selected);
  const brandTeams = teams.filter((t) => t.brand_id === selected);

  const addBrand = async (name, logoUrl) => {
    try { await api.post("/brands", { name, logo_url: logoUrl || null }); toast.success("Brand added"); setShowBrandModal(null); load(); }
    catch { toast.error("Add brand failed"); }
  };
  const updateBrand = async (id, name, logoUrl) => {
    try { await api.put(`/brands/${id}`, { name, logo_url: logoUrl || null }); toast.success("Brand updated"); setShowBrandModal(null); load(); }
    catch { toast.error("Update failed"); }
  };
  const removeBrand = async (id) => {
    if (!window.confirm("Delete this brand and all its teams, users and campaigns?")) return;
    try { await api.delete(`/brands/${id}`); toast.success("Brand removed"); setSelected(null); load(); }
    catch { toast.error("Remove failed"); }
  };
  const addTeam = async (name) => {
    try { await api.post("/teams", { name, brand_id: selected }); toast.success("Team added"); setShowTeamForm(false); load(); }
    catch { toast.error("Add team failed"); }
  };
  const removeTeam = async (id) => {
    if (!window.confirm("Delete this team and unassign its users?")) return;
    try { await api.delete(`/teams/${id}`); toast.success("Team removed"); load(); }
    catch { toast.error("Remove failed"); }
  };
  const addUser = async (teamId, email) => {
    if (!email) return;
    try { await api.post("/users", { email, team_id: teamId }); toast.success("User invited"); load(); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Add user failed"); }
  };
  const removeUser = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Remove failed"); }
  };

  return (
    <div data-testid="admin-brands-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Brands & Teams</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Organize brands, teams, and the users in them.</p>
        </div>
        <button onClick={() => setShowBrandModal({ name: "" })} className="wl-btn-primary inline-flex items-center gap-2" data-testid="add-brand-btn">
          <Plus size={16} /> Add Brand
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Brand list */}
        <aside className="col-span-4">
          <div className="wl-card-solid p-3">
            <div className="relative mb-3">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
              <input className="wl-input pl-9" placeholder="Search brands" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="brand-search" />
            </div>
            {search && (
              <div className="mb-3 flex">
                <ClearFiltersButton filters={{ search }} defaults={{ search: "" }} onClear={(v) => setSearch(v.search)} testid="brands-clear-filters" />
              </div>
            )}
            <div className="space-y-1 max-h-[68vh] overflow-y-auto">
              {loading ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />) :
                filtered.map((b) => {
                  const isActive = selected === b.id;
                  return (
                    <button key={b.id} onClick={() => setSelected(b.id)}
                      className={`w-full text-left p-3 rounded-lg flex items-center gap-3 transition-colors ${isActive ? "" : "hover:bg-[var(--bg-page)]"}`}
                      style={isActive ? { background: "var(--brand-tint)" } : {}}
                      data-testid={`brand-row-${b.id}`}>
                      <BrandLogo name={b.name} logoUrl={b.logo_url} size={36} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${isActive ? "text-[var(--brand)]" : ""}`}>{b.name}</div>
                        <div className="text-xs text-[var(--text-secondary)] mt-0.5">{b.team_count} teams · {b.user_count} users · {b.campaign_count} campaigns</div>
                      </div>
                    </button>
                  );
                })}
              {!loading && filtered.length === 0 && <div className="text-sm text-[var(--text-secondary)] p-4 text-center">No brands.</div>}
            </div>
          </div>
        </aside>

        {/* Right detail */}
        <section className="col-span-8">
          {!selectedBrand ? (
            <EmptyState title="Select a brand" subtitle="Choose a brand from the left to view its teams and users." />
          ) : (
            <div className="space-y-4">
              <div className="wl-card-solid p-5 flex items-center justify-between" data-testid="brand-detail-header">
                <div className="flex items-center gap-4">
                  <BrandLogo name={selectedBrand.name} logoUrl={selectedBrand.logo_url} size={48} />
                  <div>
                    <h2 className="font-display text-xl font-semibold">{selectedBrand.name}</h2>
                    <div className="text-xs text-[var(--text-secondary)] mt-0.5">{selectedBrand.team_count} teams · {selectedBrand.user_count} users · {selectedBrand.campaign_count} campaigns</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowBrandModal(selectedBrand)} className="wl-btn-ghost inline-flex items-center gap-1.5" data-testid="edit-brand-btn"><Edit2 size={14} /> Edit brand</button>
                  <button onClick={() => setShowTeamForm(true)} className="wl-btn-primary inline-flex items-center gap-1.5" data-testid="add-team-btn"><Plus size={14} /> Add Team</button>
                  <button onClick={() => removeBrand(selectedBrand.id)} className="wl-btn-destructive">Remove</button>
                </div>
              </div>

              {showTeamForm && (
                <TeamForm onSubmit={(name) => addTeam(name)} onCancel={() => setShowTeamForm(false)} />
              )}

              {brandTeams.length === 0 && <EmptyState title="No teams yet" subtitle="Add your first team to start inviting users." />}

              {brandTeams.map((t) => {
                const teamUsers = users.filter((u) => u.team_id === t.id);
                const teamCampaigns = campaigns.filter((c) => (c.team_ids || []).includes(t.id));
                const open = openTeamId === t.id;
                // Pick the most-recently created user in this team for team-level preview.
                // Status is irrelevant — admin previews mint their own JWT (no password needed).
                const teamPreviewUser = [...teamUsers]
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
                return (
                  <div key={t.id} className="wl-card-solid overflow-hidden" data-testid={`team-card-${t.id}`}>
                    <div className="w-full px-5 py-4 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => setOpenTeamId(open ? null : t.id)}
                        className="flex-1 text-left flex items-center justify-between gap-3 -mx-2 px-2 py-1 rounded-md hover:bg-[var(--bg-page)]"
                        data-testid={`team-card-toggle-${t.id}`}
                      >
                        <div>
                          <div className="font-medium">{t.name}</div>
                          <div className="text-xs text-[var(--text-secondary)] mt-0.5">{teamUsers.length} users · {teamCampaigns.length} campaigns</div>
                        </div>
                        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => { if (teamPreviewUser) previewAs({ teamUserId: teamPreviewUser.id, brandName: `${selectedBrand?.name} · ${t.name}` }); }}
                          disabled={!teamPreviewUser}
                          title={teamPreviewUser ? `Preview Wishlink Kiwi as ${teamPreviewUser.email}` : "Add a user to this team first"}
                          className="wl-btn-secondary inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`team-preview-${t.id}`}
                        >
                          <Eye size={13} /> Preview as team
                        </button>
                        <button type="button" onClick={() => removeTeam(t.id)} className="wl-btn-destructive" data-testid={`team-remove-${t.id}`}>Remove</button>
                      </div>
                    </div>
                    {open && (
                      <div className="grid grid-cols-2 gap-0 border-t border-[var(--border-default)]">
                        {/* Users */}
                        <div className="p-5 border-r border-[var(--border-default)]">
                          <div className="wl-label mb-3">Users</div>
                          {teamUsers.length === 0 && <div className="text-sm text-[var(--text-secondary)]">No users yet.</div>}
                          {teamUsers.map((u) => (
                            <div key={u.id} className="flex items-center justify-between py-2 text-sm border-b border-dashed border-[var(--border-dashed)] last:border-b-0" data-testid={`team-user-${u.id}`}>
                              <div>
                                <div className="font-medium">{u.email}</div>
                                <div className="text-xs text-[var(--text-secondary)]">Added {new Date(u.created_at).toLocaleDateString("en-IN")} · {u.status}</div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => previewAs({ teamUserId: u.id, brandName: selectedBrand?.name })}
                                  title="Open Wishlink Kiwi as this user"
                                  className="wl-btn-ghost text-xs inline-flex items-center gap-1"
                                  data-testid={`preview-user-${u.id}`}
                                >
                                  <Eye size={12} /> Preview
                                </button>
                                <button onClick={() => removeUser(u.id)} className="wl-btn-destructive">Remove</button>
                              </div>
                            </div>
                          ))}
                          <UserAddRow onAdd={(email) => addUser(t.id, email)} testid={`add-user-${t.id}`} />
                        </div>
                        {/* Campaigns */}
                        <div className="p-5">
                          <div className="wl-label mb-3">Assigned campaigns</div>
                          {teamCampaigns.length === 0 && <div className="text-sm text-[var(--text-secondary)]">No campaigns assigned.</div>}
                          {teamCampaigns.map((c) => (
                            <div key={c.id} className="flex items-center justify-between py-2 text-sm border-b border-dashed border-[var(--border-dashed)] last:border-b-0">
                              <div>
                                <div className="font-medium">{c.campaign_name}</div>
                                <div className="text-xs text-[var(--text-secondary)]">{c.campaign_id}</div>
                              </div>
                              <Link to={`/admin/campaigns/${c.id}/config`} className="wl-btn-ghost inline-flex items-center gap-1 text-xs">Open <ExternalLink size={12} /></Link>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {showBrandModal && (
        <BrandModal initial={showBrandModal} onClose={() => setShowBrandModal(null)} onSave={(name, logoUrl) => showBrandModal.id ? updateBrand(showBrandModal.id, name, logoUrl) : addBrand(name, logoUrl)} />
      )}
    </div>
  );
}

function UserAddRow({ onAdd, testid }) {
  const [email, setEmail] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onAdd(email); setEmail(""); }} className="flex items-center gap-2 mt-3">
      <input className="wl-input flex-1" placeholder="+ Add user email…" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={testid} />
      <button className="wl-btn-primary text-xs">Invite</button>
    </form>
  );
}

function TeamForm({ onSubmit, onCancel }) {
  const [name, setName] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(name); }} className="wl-card p-4 flex items-center gap-3" data-testid="team-form">
      <input className="wl-input flex-1" placeholder="Team name (e.g. Beauty)" value={name} onChange={(e) => setName(e.target.value)} required />
      <button type="button" className="wl-btn-ghost" onClick={onCancel}>Cancel</button>
      <button className="wl-btn-primary">Add team</button>
    </form>
  );
}

function BrandModal({ initial, onClose, onSave }) {
  const [name, setName] = useState(initial?.name || "");
  const [logoUrl, setLogoUrl] = useState(initial?.logo_url || "");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center wl-fade-in" style={{ background: "rgba(26,31,54,0.4)", backdropFilter: "blur(4px)" }}>
      <div className="wl-card-solid bg-white w-full max-w-md p-6 wl-scale-in shadow-xl">
        <h2 className="font-display text-lg font-semibold mb-4">{initial?.id ? "Edit brand" : "Add brand"}</h2>
        <form onSubmit={(e) => { e.preventDefault(); onSave(name, logoUrl); }}>
          <label className="wl-label">Brand name</label>
          <input className="wl-input mt-1 mb-4" autoFocus value={name} onChange={(e) => setName(e.target.value)} required data-testid="brand-modal-name" />
          <label className="wl-label">Brand logo</label>
          <div className="mt-1 mb-4">
            <LogoUploader value={logoUrl} onChange={setLogoUrl} name={name || "Brand"} size={56} testid="brand-logo-uploader" />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="wl-btn-ghost">Cancel</button>
            <button className="wl-btn-primary" data-testid="brand-modal-save">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}
